import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import client, { auth } from "../lib/api";
import { ArrowLeft, RefreshCw, Save, Trash2, Sparkles } from "lucide-react";

const STATUSES = ["New", "Contacted", "Qualified", "Offer Made", "Dead"];
const SCORE_CLASS = { Hot: "badge-hot", Warm: "badge-warm", Cold: "badge-cold" };

export default function AdminLeadDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [lead, setLead] = useState(null);
  const [notes, setNotes] = useState("");
  const [status, setStatus] = useState("New");
  const [followUp, setFollowUp] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");

  useEffect(() => {
    if (!auth.isAuthed()) return nav("/admin/login");
    (async () => {
      try {
        const { data } = await client.get(`/admin/leads/${id}`);
        setLead(data);
        setNotes(data.notes || "");
        setStatus(data.status || "New");
        setFollowUp(data.follow_up_at || "");
      } catch { nav("/admin"); }
    })();
  }, [id, nav]);

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await client.patch(`/admin/leads/${id}`, { status, notes, follow_up_at: followUp || null });
      setLead(data);
      setMsg("Saved.");
      setTimeout(() => setMsg(""), 1500);
    } finally { setBusy(false); }
  };

  const requalify = async () => {
    setBusy(true);
    try {
      await client.post(`/admin/leads/${id}/requalify`);
      setMsg("Re-qualifying — refresh in a few seconds.");
      setTimeout(async () => {
        const { data } = await client.get(`/admin/leads/${id}`);
        setLead(data);
        setMsg("");
      }, 3500);
    } finally { setBusy(false); }
  };

  const removeIt = async () => {
    if (!window.confirm("Delete this lead?")) return;
    await client.delete(`/admin/leads/${id}`);
    nav("/admin");
  };

  if (!lead) return <div className="p-10 text-center text-muted">Loading…</div>;

  return (
    <div className="min-h-screen bg-offwhite" data-testid="admin-lead-detail">
      <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 py-8">
        <Link to="/admin" className="inline-flex items-center gap-2 text-navy font-semibold mb-6" data-testid="back-to-inbox">
          <ArrowLeft size={16} /> Back to inbox
        </Link>

        <div className="bg-white rounded-md border border-[var(--border-soft)] p-6 sm:p-8">
          <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
            <div>
              <div className="flex items-center gap-3">
                <span className={`px-3 py-1 rounded-full text-xs font-bold ${SCORE_CLASS[lead.ai_score] || "badge-cold"}`} data-testid="lead-score-badge">{lead.ai_score}</span>
                <span className="text-xs text-muted">Received {new Date(lead.created_at).toLocaleString()}</span>
              </div>
              <h1 className="font-display text-3xl text-navy mt-2">{lead.name || "Unnamed lead"}</h1>
              <div className="text-muted text-sm">Source: <span className="font-semibold text-navy-800">{lead.source_page}</span></div>
            </div>
            <div className="flex gap-2">
              <button onClick={requalify} disabled={busy} className="h-10 px-3 rounded-md border-2 border-navy text-navy font-semibold flex items-center gap-2" data-testid="requalify-btn"><Sparkles size={14} /> Re-qualify</button>
              <button onClick={removeIt} className="h-10 px-3 rounded-md border-2 border-red-500 text-red-600 font-semibold flex items-center gap-2" data-testid="delete-lead-btn"><Trash2 size={14} /> Delete</button>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <h3 className="font-display text-lg text-navy mb-3">Property</h3>
              <dl className="text-sm space-y-2">
                <Row k="Address" v={lead.address} />
                <Row k="Location" v={[lead.city, lead.state, lead.zip].filter(Boolean).join(", ") || "—"} />
                <Row k="Est. Value" v={lead.estimated_value || "—"} />
                <Row k="Condition" v={lead.condition || "—"} />
                <Row k="Timeline" v={lead.timeline || "—"} />
                <Row k="Situation" v={lead.situation || "—"} />
                <Row k="Reason" v={lead.reason || "—"} />
              </dl>
              <h3 className="font-display text-lg text-navy mt-6 mb-3">Contact</h3>
              <dl className="text-sm space-y-2">
                <Row k="Phone" v={<a href={`tel:${lead.phone}`} className="text-money font-semibold">{lead.phone}</a>} />
                <Row k="Email" v={lead.email ? <a href={`mailto:${lead.email}`} className="text-money font-semibold">{lead.email}</a> : "—"} />
                <Row k="SMS Consent (required)" v={lead.consent_transactional_sms ? "Yes" : "No"} />
                <Row k="SMS Consent (marketing)" v={lead.consent_marketing_sms ? "Yes" : "No"} />
              </dl>
            </div>
            <div>
              <h3 className="font-display text-lg text-navy mb-3 flex items-center gap-2"><Sparkles size={16} className="text-money" /> AI Qualification</h3>
              <div className="bg-offwhite p-4 rounded-md border border-[var(--border-soft)]">
                <p className="text-sm text-navy-800" data-testid="ai-summary">{lead.ai_summary || "Awaiting AI summary…"}</p>
                {lead.ai_talking_points?.length > 0 && (
                  <>
                    <div className="text-xs font-bold uppercase tracking-wide text-money mt-4 mb-2">Talking points</div>
                    <ul className="list-disc pl-5 space-y-1 text-sm text-navy-800" data-testid="ai-talking-points">
                      {lead.ai_talking_points.map((p, i) => <li key={`${i}-${p.slice(0, 32)}`}>{p}</li>)}
                    </ul>
                  </>
                )}
              </div>

              <h3 className="font-display text-lg text-navy mt-6 mb-3">Status</h3>
              <select value={status} onChange={(e)=>setStatus(e.target.value)} className="w-full h-12 px-3 border-2 border-[var(--border-soft)] rounded-md bg-white" data-testid="status-select">
                {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>

              <label className="block text-xs font-semibold uppercase tracking-wide text-navy-800 mt-4 mb-1">Follow-up reminder</label>
              <input type="datetime-local" value={followUp?.slice(0,16) || ""} onChange={(e)=>setFollowUp(e.target.value ? new Date(e.target.value).toISOString() : "")} className="w-full h-12 px-3 border-2 border-[var(--border-soft)] rounded-md bg-white" data-testid="followup-input" />

              <label className="block text-xs font-semibold uppercase tracking-wide text-navy-800 mt-4 mb-1">Notes</label>
              <textarea value={notes} onChange={(e)=>setNotes(e.target.value)} rows={5} className="w-full p-3 border-2 border-[var(--border-soft)] rounded-md" data-testid="notes-textarea" />

              <button onClick={save} disabled={busy} className="mt-4 h-12 px-4 bg-money text-white font-bold rounded-md flex items-center gap-2 disabled:opacity-60" data-testid="save-lead-btn">
                <Save size={16} /> {busy ? "Saving…" : "Save changes"}
              </button>
              {msg && <p className="mt-2 text-sm text-money" data-testid="save-message">{msg}</p>}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex justify-between gap-4 border-b border-[var(--border-soft)] pb-1">
      <dt className="text-muted uppercase text-xs tracking-wide">{k}</dt>
      <dd className="text-navy-800 font-medium text-right">{v}</dd>
    </div>
  );
}
